import metricMetadataIcon from '../assets/metric-metadata-icon.svg';
import { useRef, useState } from "react";
import MetricMetadataModal from "./MetricMetadataModal";
import {
  DisaggregatedMetric,
  GeoDetails,
  GeoTag,
  MetricMetadata,
} from "../services/types";
import {
  calculateGeoPrefix,
  fetchMetricMetadata,
  fetchNationalData,
  fetchStateData,
  getGeoPrefix,
} from "../utils/dashboardUtils";

interface SoloIconProps {
  id: number;
  title: string;
  unit?: string;
  disaggData: DisaggregatedMetric[] | null;
  geoDetails: GeoDetails[];
  geoTag: GeoTag | null;
  svg?: string;
  showMetadataIcon?: boolean;
}

const SoloIcon = ({
  id,
  title,
  unit,
  disaggData,
  geoDetails,
  geoTag,
  svg,
  showMetadataIcon = true,
}: SoloIconProps) => {
  const [showMetadata, setShowMetadata] = useState(false);
  const [metadata, setMetadata] = useState<MetricMetadata[] | null>(null);
  const [stateData, setStateData] = useState<DisaggregatedMetric[] | null>(null);
  const [nationalData, setNationalData] = useState<DisaggregatedMetric[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const modalId = `metric-metadata-modal-${id ?? 'main'}`;

  const fetchData = async (
    fetchFunction: Function,
    setState: React.Dispatch<React.SetStateAction<any>>,
    dataState: any,
    id: number,
    disaggregated?: boolean,
    geoPrefix?: string
  ) => {
    if (dataState) return;

    setLoading(true);
    try {
      const result = await fetchFunction(id, disaggregated, geoPrefix);
      setState(result ? result.data : null);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  };

  const handleClick = async () => {
    setShowMetadata((prev) => !prev);

    const fetchPromises: Promise<void>[] = [];

    if (!metadata) {
      fetchPromises.push(
        fetchData(fetchMetricMetadata, setMetadata, metadata, id)
      );
    }

    if (unit === "percentage" || unit === "ratio") {
      const geoPrefix = geoTag?.value
        ? getGeoPrefix(geoTag.value)
        : (stateData ? null : calculateGeoPrefix(geoDetails));

      if (!stateData && geoPrefix) {
        fetchPromises.push(
          fetchData(fetchStateData, setStateData, stateData, id, false, geoPrefix)
        );
      }

      if (!nationalData) {
        fetchPromises.push(
          fetchData(fetchNationalData, setNationalData, nationalData, id, false)
        );
      }
    }

    if (fetchPromises.length > 0) {
      await Promise.all(fetchPromises);
    }
  };

  return (
    <>
      {showMetadataIcon && (
        <button
          ref={triggerRef}
          type="button"
          onClick={handleClick}
          aria-haspopup="dialog"
          aria-expanded={showMetadata ? "true" : "false"}
          aria-controls={modalId}
          aria-label={`Additional information about ${title}`}
          className="
            inline-flex h-6 w-6 items-center justify-center rounded
            transition-transform hover:scale-105 focus:scale-105
            focus-visible:outline-none
            focus-visible:ring-2 focus-visible:ring-offset-2
            focus-visible:ring-neutral-900 focus-visible:ring-offset-white
            dark:focus-visible:ring-white dark:focus-visible:ring-offset-neutral-900
          "
        >
          <img
            src={metricMetadataIcon}
            alt=""
            aria-hidden="true"
            className="h-3 w-3"
            draggable="false"
          />
        </button>
      )}

      <MetricMetadataModal
        isOpen={showMetadata}
        onClose={handleClick}
        metadata={metadata}
        disaggData={disaggData}
        stateData={stateData}
        nationalData={nationalData}
        svg={svg}
        loading={loading}
        error={error}
        triggerRef={triggerRef}
        modalId={modalId}
      />
    </>
  );
};

export default SoloIcon;